
import { useState, useEffect } from 'react';

const SquareInchFarming = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeContainer, setActiveContainer] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('square-inch-farming');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const containerTypes = [
    {
      title: 'Barrel Farming',
      description: 'Repurposed barrels transformed into productive growing systems',
      image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      icon: 'ri-archive-line',
      benefits: ['Water Efficient', 'Space Saving', 'Portable', 'Cost Effective']
    },
    {
      title: 'Container Gardens',
      description: 'Various sized containers optimized for different crops',
      image: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      icon: 'ri-inbox-line',
      benefits: ['Flexible Size', 'Easy Maintenance', 'Pest Control', 'Mobility']
    },
    {
      title: 'Raised Bed Boxes',
      description: 'Structured wooden boxes for organized square foot gardening',
      image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      icon: 'ri-layout-grid-line',
      benefits: ['Organized Layout', 'Better Drainage', 'Soil Control', 'Easy Access']
    }
  ];

  const crops = [
    { name: 'Lettuce', icon: 'ri-leaf-line', color: 'text-green-500', spacing: '4 inches' },
    { name: 'Radishes', icon: 'ri-plant-line', color: 'text-red-500', spacing: '2 inches' },
    { name: 'Herbs', icon: 'ri-seedling-line', color: 'text-emerald-500', spacing: '6 inches' },
    { name: 'Spinach', icon: 'ri-leaf-line', color: 'text-green-600', spacing: '3 inches' },
    { name: 'Carrots', icon: 'ri-plant-line', color: 'text-orange-500', spacing: '2 inches' },
    { name: 'Onions', icon: 'ri-plant-line', color: 'text-purple-500', spacing: '4 inches' }
  ];

  return (
    <section id="square-inch-farming" className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-amber-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-12 sm:mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6">
            Square Inch Farming
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Maximize your harvest in minimal space using containers, barrels, and innovative small-space growing techniques
          </p>
        </div>

        {/* Container Types */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16 transform transition-all duration-1000 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          {containerTypes.map((container, index) => (
            <div 
              key={index}
              className={`bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer ${
                activeContainer === index ? 'ring-4 ring-amber-400' : ''
              }`}
              onClick={() => setActiveContainer(index)}
            >
              <div className="relative h-48 sm:h-56 overflow-hidden">
                <img 
                  src={container.image}
                  alt={container.title}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute top-4 right-4 w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <i className={`${container.icon} text-xl sm:text-2xl text-white`}></i>
                </div>
              </div>
              
              <div className="p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">{container.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">{container.description}</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {container.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-amber-600 text-xs"></i>
                      </div>
                      <span className="text-xs sm:text-sm text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Crop Information Cards */}
        {/* <div className={`bg-white rounded-3xl p-6 sm:p-8 lg:p-12 shadow-xl mb-12 sm:mb-16 transform transition-all duration-1000 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <h3 className="text-2xl sm:text-3xl font-bold text-center text-gray-800 mb-6 sm:mb-8">Optimal Crop Spacing</h3>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6">
            {crops.map((crop, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg border-2 border-green-200 p-3 sm:p-4 text-center hover:shadow-lg transition-all duration-300 cursor-pointer"
              >
                <div className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center mx-auto mb-2 ${crop.color}`}>
                  <i className={`${crop.icon} text-lg sm:text-xl`}></i>
                </div>
                <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-1">{crop.name}</h4>
                <span className="text-xs text-gray-500">{crop.spacing}</span>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-6 sm:mt-8">
            <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">
              Each container can be optimized for specific crops with proper spacing
            </p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-gray-500">
              <span>• Container Farming</span>
              <span>• Perfect for beginners</span>
              <span>• Easy maintenance</span>
            </div>
          </div>
        </div> */}

        {/* Benefits Section */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center transform transition-all duration-1000 delay-600 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="order-2 lg:order-1">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6">Why Square Inch Farming?</h3>
            <div className="space-y-3 sm:space-y-4">
              {[
                { title: 'Maximum Efficiency', desc: 'Get more yield per square foot than traditional farming' },
                { title: 'Water Conservation', desc: 'Precise watering reduces waste by up to 80%' },
                { title: 'Pest Management', desc: 'Easier to monitor and control in contained spaces' },
                { title: 'Year-Round Growing', desc: 'Move containers indoors during harsh weather' },
                { title: 'Perfect for Beginners', desc: 'Simple, organized approach to gardening' }
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                  </div>
                  <div>
                    <h4 className="text-sm sm:text-base font-semibold text-gray-800">{benefit.title}</h4>
                    <p className="text-sm sm:text-base text-gray-600">{benefit.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative order-1 lg:order-2">
            <img 
              src="https://images.unsplash.com/photo-1416879595882-3373a0480b5b?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
              alt="Container farming setup"
              className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-2xl shadow-xl"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-amber-600/20 to-transparent rounded-2xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SquareInchFarming;
